import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Card } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { ShieldAlert, FileClock, UserPlus, Trash2, Shield, User } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function UmumTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getSettings().then(setSettings).finally(() => setLoading(false));
  }, []);

  return (
    <Card className="p-5" data-testid="data-permanence-card">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-emerald-500/10 text-emerald-600">
          <ShieldAlert className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-sm font-semibold">Data Tersimpan Permanen</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Semua data (Saldo Awal, QC, PRF, Penerimaan, edit harian, dan data mentah Data LIS
            yang diimpor) disimpan permanen di database MongoDB. Kolom harian 1-31 pada
            Pemantauan Stok dihitung langsung dari data ini, sehingga <b>tidak ada fitur auto-hapus</b>{' '}
            yang berjalan — data tidak akan hilang saat server restart, deployment ulang, atau saat
            periode baru dibuat. Data hanya terhapus bila dihapus manual (mis. hapus
            source file LIS atau hapus periode).
          </p>
        </div>
      </div>
      {loading ? (
        <Skeleton className="mt-4 h-6 w-64" />
      ) : settings?.last_cleanup ? (
        <p className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="retention-last-cleanup-history">
          <FileClock className="h-3.5 w-3.5" />
          Riwayat pembersihan otomatis (fitur lama, sudah dinonaktifkan): terakhir{' '}
          {new Date(settings.last_cleanup.created_at).toLocaleString('id-ID')} ·{' '}
          {settings.last_cleanup.deleted} baris dihapus.
        </p>
      ) : null}
    </Card>
  );
}

function ManajemenUserTab() {
  const [users, setUsers] = useState([]);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('User');
  const [loading, setLoading] = useState(false);

  const fetchUsers = async () => {
    try {
      const token = JSON.parse(localStorage.getItem('labstock_user'))?.token;
      const res = await fetch(`${API_BASE_URL}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setUsers(await res.json());
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleAddUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: newUsername, password: newPassword, role: newRole })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Gagal menambah user');
      }
      setNewUsername('');
      setNewPassword('');
      fetchUsers();
      alert('User berhasil ditambahkan!');
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (uname) => {
    if (!window.confirm(`Yakin ingin menghapus user ${uname}?`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/users/${uname}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Gagal menghapus user');
      }
      fetchUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <Card className="p-5">
      <div className="mb-4">
        <h3 className="text-sm font-semibold">Manajemen Akun & Hak Akses</h3>
        <p className="text-xs text-muted-foreground mt-0.5">Kelola akun staf, koordinator, dan admin laboratorium.</p>
      </div>

      {/* Form Tambah User */}
      <form onSubmit={handleAddUser} className="mb-5 grid grid-cols-1 md:grid-cols-4 gap-3 bg-slate-50 p-3.5 rounded-lg dark:bg-slate-800/50">
        <input
          type="text"
          placeholder="Username baru"
          required
          value={newUsername}
          onChange={(e) => setNewUsername(e.target.value)}
          className="rounded-lg border px-3 py-2 text-xs bg-white dark:bg-slate-900 dark:border-slate-700 outline-none focus:ring-1 focus:ring-teal-500"
        />
        <input
          type="password"
          placeholder="Password"
          required
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="rounded-lg border px-3 py-2 text-xs bg-white dark:bg-slate-900 dark:border-slate-700 outline-none focus:ring-1 focus:ring-teal-500"
        />
        <select
          value={newRole}
          onChange={(e) => setNewRole(e.target.value)}
          className="rounded-lg border px-3 py-2 text-xs bg-white dark:bg-slate-900 dark:border-slate-700 outline-none focus:ring-1 focus:ring-teal-500"
        >
          <option value="User">User (Read-Only)</option>
          <option value="Koordinator">Koordinator</option>
          <option value="Admin">Admin</option>
        </select>
        <button
          type="submit"
          disabled={loading}
          className="flex items-center justify-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-xs font-semibold text-white hover:bg-teal-700 transition-colors"
        >
          <UserPlus className="h-3.5 w-3.5" /> Tambah User
        </button>
      </form>

      {/* Tabel Daftar User */}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="border-b bg-slate-50 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
            <tr>
              <th className="p-2.5">Username</th>
              <th className="p-2.5">Role / Hak Akses</th>
              <th className="p-2.5 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y dark:divide-slate-800">
            {users.map((u) => (
              <tr key={u.username}>
                <td className="p-2.5 font-medium flex items-center gap-2 text-slate-900 dark:text-white">
                  <User className="h-3.5 w-3.5 text-slate-400" /> {u.username}
                </td>
                <td className="p-2.5">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                    u.role === 'Admin' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                    u.role === 'Koordinator' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                    'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                  }`}>
                    <Shield className="h-3 w-3" /> {u.role}
                  </span>
                </td>
                <td className="p-2.5 text-right">
                  {u.username !== 'raihan' && (
                    <button
                      onClick={() => handleDeleteUser(u.username)}
                      className="rounded p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                      title="Hapus User"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function Pengaturan() {
  const user = JSON.parse(localStorage.getItem('labstock_user') || '{}');
  const isAdmin = user?.role === 'Admin';

  return (
    <div className="max-w-3xl space-y-4">
      <UmumTab />
      {isAdmin && <ManajemenUserTab />}
    </div>
  );
}
